'use client';

/**
 * @fileoverview `useDiscoverEventArgs` — the single client-side path every
 * Discover subscriber uses to build its `useDiscoverEventsQuery` /
 * `useDiscoverFacetsQuery` args (WEB-450).
 *
 * Before WEB-450 each subscriber called `buildDiscoverQueryArgs(params, city)`
 * directly. Threading the connected viewer id into the cache key means EVERY
 * mounted subscriber must inject the *same* id or a connected cold load would
 * open a second cache entry and fire a duplicate `/events/discover` request
 * (the exact regression the cold-load invariant guards against). Funnelling all
 * subscribers through this one hook — which reads the id from the shared
 * {@link useDiscoverViewerId} context — makes that impossible to get wrong:
 * there is one place the id is read, so all subscribers key identically.
 *
 * Anonymous viewers resolve `viewerId === undefined`, so the produced args are
 * byte-identical to the pre-WEB-450 shape and the anonymous single-fetch
 * invariant is untouched.
 */

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import type { DiscoverEventsQueryArgs } from '@loci/api';

import { buildDiscoverQueryArgs } from '@/lib/discover/queryArgs';
import type { DiscoverParams } from '@/lib/discover/url-state';
import { useDiscoverViewerId } from '@/lib/discover/DiscoverViewerIdContext';

/**
 * Build the memoized `DiscoverEventsQueryArgs` for a discover subscription,
 * injecting the connected viewer id from context so the cache key separates
 * authenticated and anonymous queries while every subscriber stays coalesced
 * onto one entry.
 *
 * @param params   Canonical URL-derived discover params.
 * @param cityCode Active city.
 * @param cursor   Pagination cursor; keep `null` for the cold-load key.
 */
export function useDiscoverEventArgs(
  params: DiscoverParams,
  cityCode: CityCode,
  cursor: string | null = null,
): DiscoverEventsQueryArgs {
  const viewerId = useDiscoverViewerId();
  return React.useMemo(
    () => buildDiscoverQueryArgs(params, cityCode, cursor, undefined, viewerId),
    [params, cityCode, cursor, viewerId],
  );
}
